import { Component, inject, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { ChargerPropertySection } from '../../models';
import { ChargersService } from '../chargers.service';


@Component({
	selector: 'app-charger-diagnostics',
	imports: [MatListModule, MatDividerModule, MatProgressSpinnerModule, MatCardModule],
	templateUrl: './charger-diagnostics.component.html',
	styleUrl: './charger-diagnostics.component.scss'
})
export class ChargerDiagnosticsComponent {
	chargerId!: string;
	chargerPropertySections: ChargerPropertySection[] = [];
	chargerPropertiesLoaded = signal<boolean>(false);

	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private chargersService = inject(ChargersService);

	constructor() { }

	ngOnInit() {
		this.chargerId = this.route.parent?.snapshot.paramMap.get('id') ?? '';
		const isValid = /^[PD]\d{14}$/.test(this.chargerId);
		if (!isValid) {
			this.router.navigate(['/404']);
		}

		const NA = $localize`:@@NA:n/a`;
		const YES = $localize`:@@Yes:Yes`;
		const NO = $localize`:@@No:No`;

		this.chargersService.getChargerDiagnostics(this.chargerId).subscribe((charger) => {
			console.log('getChargerDiagnostics():', charger);

			// @@@ change to signal to have real time updates
			if (charger.productInformation?.mainController) {
				this.chargerPropertySections.push({
					title: $localize`:@@MainController:Main Controller`,
					items: [
						{
							id: 'hardwareType',
							name: $localize`:@@HardwareType:Hardware Type`,
							value: charger.productInformation.mainController.hardwareType || NA
						},
						{
							id: 'hardwareVersion',
							name: $localize`:@@HardwareVersion:Hardware Version`,
							value: charger.productInformation.mainController.hardwareVersion || NA
						},
						{
							id: 'firmwareVersion',
							name: $localize`:@@FirmwareVersion:Firmware Version`,
							value: charger.productInformation.mainController.firmwareVersion || NA
						},
					]
				});
			}

			if (charger.productInformation?.connectivityController) {
				this.chargerPropertySections.push({
					title: $localize`:@@ConnectivityController:Connectivity Controller`,
					items: [
						{
							id: 'serialNumber',
							name: $localize`:@@PNumber:P-Number`,
							value: charger.productInformation.connectivityController.serialNumber || NA
						},
						{
							id: 'hardwareVersion',
							name: $localize`:@@HardwareVersion:Hardware Version`,
							value: charger.productInformation.connectivityController.hardwareVersion || NA
						},
						{
							id: 'firmwareVersion',
							name: $localize`:@@FirmwareVersion:Firmware Version`,
							value: charger.productInformation.connectivityController.firmwareVersion || NA
						},
						{
							id: 'connectionMedium',
							name: $localize`:@@ConnectionMedium:Connection Medium`,
							value: charger.networkStatus?.connectionMedium || NA
						},
					]
				});
			}

			if (charger.networkStatus?.wifi) {
				this.chargerPropertySections.push({
					title: $localize`:@@Wifi:WiFi`,
					items: [
						{
							id: 'configuredSsid',
							name: $localize`:@@ConfiguredSSID:Configured SSID`,
							value: charger.networkStatus.wifi.configuredSsid || NA
						},
						{
							id: 'connected',
							name: $localize`:@@Connected:Connected`,
							value: (charger.networkStatus.wifi.connected) ? YES : NO
						},
						{
							id: 'address',
							name: $localize`:@@IPv4Address:IPv4 Address`,
							value: charger.networkStatus.wifi.ipv4?.address || NA
						},
						{
							id: 'netmask',
							name: $localize`:@@IPv4Netmask:IPv4 Netmask`,
							value: charger.networkStatus.wifi.ipv4?.netmask || NA
						},
						{
							id: 'gateway',
							name: $localize`:@@IPv4Gateway:IPv4 Gateway`,
							value: charger.networkStatus.wifi.ipv4?.gateway || NA
						},
						{
							id: 'rssi',
							name: $localize`:@@RSSI:RSSI` + ' [dBm]',
							value: charger.networkStatus.wifi.rssi?.toString() || NA
						},
					]
				});
			}

			if (charger.networkStatus?.ethernet) {
				this.chargerPropertySections.push({
					title: $localize`:@@Ethernet:Ethernet`,
					items: [
						{
							id: 'connected',
							name: $localize`:@@Connected:Connected`,
							value: (charger.networkStatus.ethernet.connected) ? YES : NO
						},
						{
							id: 'address',
							name: $localize`:@@IPv4Address:IPv4 Address`,
							value: charger.networkStatus.ethernet.ipv4?.address || NA
						},
						{
							id: 'netmask',
							name: $localize`:@@IPv4Netmask:IPv4 Netmask`,
							value: charger.networkStatus.ethernet.ipv4?.netmask || NA
						},
						{
							id: 'gateway',
							name: $localize`:@@IPv4Gateway:IPv4 Gateway`,
							value: charger.networkStatus.ethernet.ipv4?.gateway || NA
						},
					]
				});
			}

			if (charger.backendStatus) {
				this.chargerPropertySections.push({
					title: $localize`:@@Backend:Back-end`,
					items: [
						{
							id: 'connected',
							name: $localize`:@@Connected:Connected`,
							value: (charger.backendStatus.connected) ? YES : NO
						},
					]
				});
			}

			if (charger.ocppStatus) {
				this.chargerPropertySections.push({
					title: $localize`:@@OCPP:OCPP`,
					items: [
						{
							id: 'connected',
							name: $localize`:@@Connected:Connected`,
							value: (charger.ocppStatus.connected) ? YES : NO
						},
						{
							id: 'enabled',
							name: $localize`:@@Enabled:Enabled`,
							value: (charger.ocppStatus.enabled) ? YES : NO
						},
						{
							id: 'centralSystem',
							name: $localize`:@@CPMS:CPMS`,
							value: charger.ocppStatus.cpms?.centralSystem || NA
						},
						{
							id: 'url',
							name: $localize`:@@CPMSUrl:CPMS URL`,
							value: charger.ocppStatus.cpms?.url || NA
						},
					]
				});
			}

			console.log('chargerPropertySections:', this.chargerPropertySections);
			this.chargerPropertiesLoaded.set(true);
		});
	}
}
